import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { SEAT_RADIUS } from "modules/event";
import { selectTheaterTemplate } from "modules/theater/selectors";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { AdminSeat } from "@remo-co/ui-core/src/icons/AdminSeat";
import { selectIsEventManager } from "modules/event/selectors";
import { getMapTemplateByType } from "modules/event/template";
import { makeSelectUserRoomStatusByRoomId } from "modules/room/redux/selectors";
import { IRoom, ISeatPosition } from "types";
import { selectLiveUsers } from "modules/eventUserPresence/redux/selectors";
import { MapAvatarComponent } from "../MapAvatarComponent";
import { useStyles as useRoomStyles } from "../RoomComponent/styles";
import { useOverflowPositions } from "./useOverflowSeating";

interface Props {
  room: IRoom;
  isHovering: boolean;
}

const Seats = ({ room, isHovering }: Props): JSX.Element | null => {
  const roomStyles = useRoomStyles({});
  const liveUsers = useSelector(selectLiveUsers);
  const { positions, adminSeats = [] } = room;
  const theaterTemplate = useSelector(selectTheaterTemplate);
  const spaceConfig = getMapTemplateByType(theaterTemplate);
  const usersInside = useSelector(makeSelectUserRoomStatusByRoomId(room.id));

  const { overflowPositions } = useOverflowPositions(
    [...room.positions, ...adminSeats],
    Math.floor(room.positions.length * 0.5),
  );

  const hasEventManagerAccess = useSelector(selectIsEventManager);
  const seatRadius = (spaceConfig?.seatRadius ?? SEAT_RADIUS) * 2;

  const usersAtTable = useMemo(() => {
    if (!usersInside) {
      return [];
    }

    return Object.entries(usersInside).map(([userId, userPosition]) => {
      const {
        position,
        isAdminSeat = false,
        isOverflow = false,
      } = userPosition;

      if (isAdminSeat) {
        return { userId, seat: adminSeats?.[position] };
      }
      if (isOverflow) {
        return { userId, seat: overflowPositions?.[position] };
      }

      return { userId, seat: positions[position] };
    });
  }, [usersInside, adminSeats, positions, overflowPositions]);

  if (!adminSeats && usersAtTable.length === 0) {
    return null;
  }

  return (
    <>
      {usersAtTable
        .filter(
          (
            user,
          ): user is {
            userId: string;
            isAdminSeat: boolean;
            seat: ISeatPosition;
            isOverflow: false;
          } => Boolean(user.seat) && liveUsers.includes(user.userId),
        )
        .map(({ userId, seat }) => (
          <MapAvatarComponent
            key={`${seat.x}${seat.y}`}
            x={seat.x}
            y={seat.y}
            room={room}
            userId={userId}
            isHoveringOnTable={isHovering}
          />
        ))}
      {hasEventManagerAccess &&
        adminSeats &&
        adminSeats.map((adminSeat) => (
          <Container
            key={`${adminSeat.x}${adminSeat.y}`}
            className={roomStyles.adminSeatContainer}
            style={{
              top: adminSeat.y,
              left: adminSeat.x,
              width: seatRadius,
              height: seatRadius,
            }}
          >
            <IconButton
              className={roomStyles.adminSeat}
              data-testid="admin-seat-button"
              tabIndex={-1}
              size="large"
            >
              <AdminSeat />
            </IconButton>
          </Container>
        ))}
    </>
  );
};

export default memo(Seats);
